import { useCallback, useRef, useState } from "react";

/**
 * Adapts a query's `refetch` into the shape `ScreenLayout.Body` expects for
 * pull-to-refresh.
 *
 * Tracks its own `refreshing` flag rather than reusing the query's `loading`,
 * so the pull spinner doesn't fire on first mount where the screen already
 * shows a skeleton. Refetch errors are swallowed here on purpose: the query's
 * own `error` still updates, and each screen renders that through its existing
 * error state.
 *
 * A screen with more than one query passes a combined callback:
 *
 *   usePullToRefresh(() => Promise.all([refetchA(), refetchB()]))
 */
export function usePullToRefresh(refetch: () => Promise<unknown>) {
  const [refreshing, setRefreshing] = useState(false);

  const inFlightRef = useRef(false);

  const onRefresh = useCallback(() => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setRefreshing(true);

    refetch()
      .catch(() => undefined)
      .finally(() => {
        inFlightRef.current = false;
        setRefreshing(false);
      });
  }, [refetch]);

  return { refreshing, onRefresh };
}
